import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from '@remix-run/react';
import { SupabaseClient } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import arrowRight from '~/assets/arrow-right.svg';
import { Button } from '~/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '~/components/ui/input-otp';
import { SocialLoginButton } from '~/components/ui/social-login-button';
import useTranslation from '~/i18n/i18n';
import { useSupabase } from '~/lib/providers/supabase-provider';
import { useUserSettingsStore } from '~/lib/stores/user-settings-store';
import { redirectPathnameAfterLogin } from '~/lib/utils/constants';
import { metadataMeta } from '~/lib/utils/meta-utils-client';
import { metadataLoader } from '~/lib/utils/meta-utils-server';

export const loader = metadataLoader((t) => ({
  title: t('common.routes.login.title'),
  description: t('common.routes.login.description'),
}));

export const meta = metadataMeta<typeof loader>();

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supabase = useSupabase();

  const [email, setEmail] = useState<string | null>();

  const redirectTo = searchParams.get('redirect') || redirectPathnameAfterLogin;

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-y-4">
      {!email && (
        <EmailForm
          supabase={supabase}
          onSuccess={(email) => {
            setEmail(email);
          }}
          redirectTo={redirectTo}
        />
      )}
      {email && (
        <OTPForm
          supabase={supabase}
          email={email}
          onSuccess={() => {
            navigate(redirectTo);
          }}
        />
      )}
    </div>
  );
}

interface EmailFormProps {
  supabase: SupabaseClient;
  onSuccess: (email: string) => void;
  redirectTo: string;
}

const emailFormSchema = z.object({
  email: z
    .string()
    .email('auth.errors.invalidEmail')
    .min(1, 'auth.errors.empty'),
});

const EmailForm = ({ supabase, onSuccess, redirectTo }: EmailFormProps) => {
  const { t, i18n } = useTranslation();
  const { referralCode, clearReferralCode } = useUserSettingsStore();

  const form = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: {
      email: '',
    },
  });

  const signInWithGoogle = () => {
    // Construct redirect URL. If referral code is set, add it to the URL search
    // parameters.
    const redirectUrl = new URL('/auth/callback', window.location.href);
    if (referralCode) {
      redirectUrl.searchParams.set('referral_code', referralCode);
    }
    // Preserve the redirect parameter for after authentication
    redirectUrl.searchParams.set('redirect', redirectTo);

    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl.toString() },
    });
  };

  // Function to send the OTPd
  const { mutate: signIn, isLoading } = useMutation({
    mutationFn: async (data: { email: string }) => {
      const { error } = await supabase.auth.signInWithOtp({
        ...data,
        options: {
          data: {
            locale: i18n.language,
            referral_code: referralCode,
          },
        },
      });

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      // Remove the referral code from local storage
      clearReferralCode();

      onSuccess(data.email);
    },
    onError: (error) => {
      console.error(t('auth.errors.failedToSendOTP'), error);
      toast.error(t('auth.errors.failedToSendOTP'));
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => signIn(data))}
        className="flex w-full flex-col items-center gap-y-12">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  placeholder={t('auth.emailPlaceholder')}
                  className="text-lg"
                  type="email"
                  {...field}
                />
              </FormControl>
              <FormDescription className="text-xs">
                {t('auth.weWillSendYouOTP')}{' '}
                {t('auth.agreeToTermsAndConditions')}{' '}
                <Link
                  to="/terms-conditions"
                  className="underline hover:no-underline">
                  {t('auth.termsAndConditions')}
                </Link>
                .
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={!form.formState.isValid}
          isLoading={isLoading}
          type="submit"
          className="group flex w-full flex-row gap-4 py-5">
          {t('auth.sendCode')}
          <img
            src={arrowRight}
            alt=""
            className="transition-all duration-300 group-hover:translate-x-4 dark:invert group-hover:dark:invert-0"
          />
        </Button>

        <div className="flex w-full flex-row items-center justify-center gap-8 text-sm uppercase">
          <div className="h-px grow bg-black dark:bg-white" />
          {t('auth.or')}
          <div className="h-px grow bg-black dark:bg-white" />
        </div>

        <div className="flex w-full flex-col gap-4">
          <SocialLoginButton
            onClick={(e) => {
              e.preventDefault();
              signInWithGoogle();
            }}
            provider="google"
          />
          <FormDescription className="text-xs">
            {t('auth.agreeToTermsAndConditions')}{' '}
            <Link
              to="/terms-conditions"
              className="underline hover:no-underline">
              {t('auth.termsAndConditions')}
            </Link>
            .
          </FormDescription>
        </div>
      </form>
    </Form>
  );
};

const otpFormSchema = z.object({
  token: z.string().min(1, 'auth.errors.empty'),
});

interface OTPFormProps {
  supabase: SupabaseClient;
  email: string;
  onSuccess: () => void;
}

const OTPForm = ({ supabase, email, onSuccess }: OTPFormProps) => {
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof otpFormSchema>>({
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      token: '',
    },
  });

  // Function to verify the OTP
  const { mutate: verify, isLoading } = useMutation({
    mutationFn: async (otpData: { email: string; token: string }) => {
      const { data, error } = await supabase.auth.verifyOtp({
        ...otpData,
        type: 'email',
      });

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (error) => {
      console.error(t('auth.errors.wrongOTP'), error);
      toast.error(t('auth.errors.wrongOTP'));
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) => verify({ email, ...data }))}
        className="flex w-full flex-col items-center space-y-8 md:w-fit">
        <FormField
          control={form.control}
          name="token"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{t('auth.otp')}</FormLabel>
              <FormControl>
                <InputOTP maxLength={6} {...field}>
                  <InputOTPGroup
                    className="flex w-full gap-4 md:w-fit"
                    inputMode="numeric">
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription className="text-xs">
                {t('auth.checkYourEmail')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          disabled={!form.formState.isValid}
          isLoading={isLoading}
          type="submit"
          className="w-full">
          {t('auth.verify')}
        </Button>
      </form>
    </Form>
  );
};
